/**
 * Product data from PHP API only - real data from /api/article.php (table article)
 * Uses in-memory cache to make repeat visits / navigations instant (stale-while-revalidate).
 */

import { getApiUrl } from '../config/api';

const CACHE_KEY = 'kissariti_product_list';
const CACHE_TTL_MS = 3 * 60 * 1000; // 3 minutes
/** Invalidates in-memory list when a new JS bundle is deployed */
const BUILD_ID =
  typeof process !== 'undefined' && process.env && process.env.REACT_APP_VERSION
    ? String(process.env.REACT_APP_VERSION)
    : '';

let memoryCache = { data: null, ts: 0, buildId: BUILD_ID };

function isCacheValid() {
  return (
    memoryCache.data != null &&
    memoryCache.buildId === BUILD_ID &&
    Date.now() - memoryCache.ts < CACHE_TTL_MS
  );
}

const arrayBufferToBase64 = (buffer) => {
  const bytes = new Uint8Array(buffer);
  const binaryString = Array.from(bytes)
    .map((byte) => String.fromCharCode(byte))
    .join("");
  return btoa(binaryString);
};

function mapApiProductToFrontend(dataIndex) {
  const originPrice = dataIndex.type_remise
    ? dataIndex.type_remise === "pour cent"
      ? Number(dataIndex.prix) - (Number(dataIndex.prix) * (Number(dataIndex.valeur_remise) / 100))
      : Number(dataIndex.prix) - Number(dataIndex.valeur_remise)
    : dataIndex.prix;
  return {
    id: dataIndex.code_article,
    category: isNaN(dataIndex.famille) ? dataIndex.famille : (dataIndex.category_nom || dataIndex.famille),
    name: dataIndex.designation,
    new: false,
    sale: true,
    rate: dataIndex.valeur_remise,
    price: dataIndex.prix,
    originPrice,
    brand: "",
    sold: dataIndex.quantité_article,
    quantity: dataIndex.quantité_article,
    out_of_stock: Number(dataIndex.out_of_stock ?? 0),
    stock_mode: String(dataIndex.stock_mode || "auto").toLowerCase(),
    quantityPurchase: 1,
    sizes: ['x', 'x'],
    variation: [],
    description: dataIndex.description,
    slug: dataIndex.type_livraison,
    images: ["/default.png"],
    thumbImage: ["/default.png"],
    action: "quick shop",
  };
}

function parseJsonOrEmpty(text) {
  const trimmed = (text || '').trim();
  if (!trimmed || trimmed.startsWith('<')) return null;
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

export async function fetchProductText() {
  if (isCacheValid()) {
    return memoryCache.data;
  }
  try {
    const response = await fetch(getApiUrl('PRODUCT_LIST'));
    const text = await response.text();
    const data = parseJsonOrEmpty(text);
    if (data === null) {
      console.warn('Product API returned non-JSON. Ensure /api/article.php is deployed and returns JSON.');
      return [];
    }
    if (!response.ok) throw new Error(data.error || `HTTP ${response.status}`);
    if (data.error) throw new Error(data.error);
    const products = data.products || [];
    const mapped = products.map(mapApiProductToFrontend);
    memoryCache = { data: mapped, ts: Date.now(), buildId: BUILD_ID };
    return mapped;
  } catch (error) {
    if (memoryCache.data != null) return memoryCache.data;
    console.error("Error fetching text data:", error);
    throw error;
  }
}

/** Return cached list immediately if available; then fetch in background and call onUpdate when fresh data arrives. */
export function fetchProductTextWithCache(onUpdate) {
  if (isCacheValid() && memoryCache.data != null) {
    onUpdate(memoryCache.data);
    refreshProductListInBackground().then((fresh) => {
      if (fresh != null) onUpdate(fresh);
    }).catch(() => {});
    return;
  }
  fetchProductText().then(onUpdate).catch(() => {
    onUpdate(memoryCache.data != null ? memoryCache.data : []);
  });
}

async function refreshProductListInBackground() {
  try {
    const response = await fetch(getApiUrl('PRODUCT_LIST'));
    const text = await response.text();
    const data = parseJsonOrEmpty(text);
    if (data === null || !response.ok) return null;
    const products = (data.products || []).map(mapApiProductToFrontend);
    memoryCache = { data: products, ts: Date.now(), buildId: BUILD_ID };
    return products;
  } catch {
    return null;
  }
}

export async function fetchProductImages(products) {
  try {
    const productItemUrl = getApiUrl('PRODUCT_ITEM');
    const productsToLoad = products.slice(0, 20);

    const updatedProducts = await Promise.allSettled(
      productsToLoad.map(async (product) => {
        try {
          const response = await fetch(`${productItemUrl}?id=${encodeURIComponent(product.id)}`);
          const text = await response.text();
          const data = parseJsonOrEmpty(text);
          if (data === null || !response.ok) return product;

          const p = data.product;
          if (!p) return product;

          const hasImage1 = p.image_1 && (p.image_1.data || p.image_1);
          const img1Base64 = p.image_1 && p.image_1.data && !p.image_1.isUrl
            ? (typeof p.image_1.data === 'string' ? p.image_1.data : arrayBufferToBase64(p.image_1.data))
            : null;
          const img1Url = p.image_1 && (p.image_1.isUrl || typeof p.image_1.data === 'string') ? p.image_1.data : null;

          const image1 = img1Base64
            ? `data:${p.image_1_type || 'image/jpeg'};base64,${img1Base64}`
            : img1Url || "/default.png";

          return {
            ...product,
            images: [image1].filter(Boolean),
            thumbImage: [image1],
          };
        } catch (error) {
          console.warn(`Error fetching images for product ${product.id}:`, error);
          return { ...product, images: ["/default.png"], thumbImage: ["/default.png"] };
        }
      })
    );

    const successfulProducts = updatedProducts
      .map((result) => (result.status === 'fulfilled' ? result.value : null))
      .filter(Boolean);
    const remainingProducts = products.slice(20).map((product) => ({
      ...product,
      images: ["/default.png"],
      thumbImage: ["/default.png"],
    }));

    return [...successfulProducts, ...remainingProducts];
  } catch (error) {
    console.error("Error fetching images:", error);
    return products.map((product) => ({
      ...product,
      images: ["/default.png"],
      thumbImage: ["/default.png"],
    }));
  }
}

export async function productList() {
  try {
    const response = await fetch(getApiUrl('PRODUCT_LIST'));
    const text = await response.text();
    const data = parseJsonOrEmpty(text);
    if (data === null) {
      console.warn('Product API returned non-JSON. Ensure /api/article.php is deployed and returns JSON.');
      return [];
    }
    if (!response.ok) throw new Error(data.error || `HTTP ${response.status}`);
    if (data.error) throw new Error(data.error);
    const products = data.products || [];

    return products.map((dataIndex) => {
      const base = mapApiProductToFrontend(dataIndex);
      base.thumbImage = ["/default.png"];
      base.images = ["/default.png"];
      base.sold = 4;
      return base;
    });
  } catch (error) {
    console.error("Error fetching data:", error);
    throw error;
  }
}
